import React, { useState, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import ItemId from "../../../views/shopping/Orders/ItemId";
import { getImage } from "../../../views/shopping/Cart/CartItemFields";
import Dimensions from "../../../views/inventory/requests/Dimensions";
import useMutation from "../../../hooks/useMutation";
import {
  ADD_SHOPPING_INVENTORY_ITEM_MUTATION,
  ADD_INVENTORY_ITEM_MUTATION,
} from "../../../constants/graphql";
import { useShopping } from "../../../contexts/shopping";
import AssetQuantityField from "../../../views/inventory/requests/AssetQuantityField";
import useAuth from "../../../hooks/useAuth";
import useQuery from "../../../hooks/useQuery";

export default function AddAssetsModal({ title, assets, onClose, processNewItem }) {
  const { user } = useAuth();
  const query = useQuery();
  const [list, setList] = useState(assets.map(v => ({ id: v.id, quantity: v.quantity })));
  const [adding, setAdding] = useState(false);

  // Determine if we're in inventory request context
  const inventoryRequestId =
    query.get("inventoryRequestId") || query.get("orderPicking") || query.get("id");
  const isInventoryRequest = !!inventoryRequestId;

  // Use appropriate mutation based on context
  const mutation = isInventoryRequest
    ? ADD_INVENTORY_ITEM_MUTATION
    : ADD_SHOPPING_INVENTORY_ITEM_MUTATION;
  const [addInventoryItem] = useMutation(mutation);
  const addInFlightRef = useRef(false);

  const { setInventoryAsset, setInventoryItem, removeInventoryItem } = useShopping();
  const disabled = false;

  const dimensionsPresent = dimensions => {
    if (!dimensions || !Array.isArray(dimensions) || dimensions.length === 0) {
      return false;
    }

    return dimensions.some(dim => {
      const depth = parseFloat(dim.depth || 0);
      const width = parseFloat(dim.width || 0);
      const height = parseFloat(dim.height || 0);
      return depth > 0 || width > 0 || height > 0;
    });
  };

  const modelPresent = model => {
    return model && model.trim().length > 0;
  };

  const columnVisibility = useMemo(() => {
    const showModel = assets.some(asset => modelPresent(asset.model));
    const showDimensions = assets.some(asset => dimensionsPresent(asset.dimensions));

    return {
      showModel,
      showDimensions,
    };
  }, [assets]);

  const handleChangeQuantity = (id, quantity) => {
    setList(v => v.map(v => (v.id === id ? { ...v, quantity } : v)));
  };

  const handleRemove = id => {
    setList(v => v.filter(v => v.id !== id));
  };

  const AddToCart = async () => {
    if (addInFlightRef.current) return;

    addInFlightRef.current = true;
    setAdding(true);
    try {
      for (const value of list) {
        const asset = assets?.find(v => v.id === value.id) || {};

        let variables;

        if (isInventoryRequest) {
          variables = {
            assetId: asset.id,
            inventoryRequestId: inventoryRequestId,
            quantity: value.quantity,
            pickedStatus: false,
          };

          if (processNewItem) {
            variables = processNewItem(variables) || variables;
          }
        } else {
          variables = {
            assetId: asset.id,
            quantity: value.quantity,
            location: value.location || "",
          };
        }

        await addInventoryItem({
          variables,
          onSuccess: ({ inventoryItem, asset }) => {
            if (!isInventoryRequest) {
              if (inventoryItem && asset) {
                setInventoryAsset(asset);
              }
              inventoryItem ? setInventoryItem(inventoryItem) : removeInventoryItem(asset.id);
            }
            setList(prevList => prevList.filter(item => item.id !== value.id));
          },
        });
      }
      onClose();
    } finally {
      addInFlightRef.current = false;
      setAdding(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} aria-labelledby="dialog-title">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box />
          <Typography fontSize={22} fontWeight={500} color="textPrimary" component="span">
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Box my={2}>
            {assets.length ? (
              <Table stickyHeader>
                <TableHead>
                  <TableCell align="center">Image</TableCell>
                  <TableCell align="center">Item ID</TableCell>
                  <TableCell align="center">Description</TableCell>
                  {columnVisibility.showModel && <TableCell align="center">Model</TableCell>}
                  {columnVisibility.showDimensions && (
                    <TableCell align="center">Dimensions</TableCell>
                  )}
                  <TableCell align="center">{user.company.settings.quantity_label}</TableCell>
                  <TableCell align="center" width={170}>
                    Quantity to Add
                  </TableCell>
                  <TableCell align="center" />
                </TableHead>
                <TableBody>
                  {list.map(value => {
                    const asset = assets?.find(v => v.id === value.id) || {};
                    const photo = getImage(asset);
                    const highlight = value.quantity > asset?.availableQuantity;
                    const background = highlight ? "#fffbf2" : undefined;
                    return (
                      <TableRow key={asset.id} style={{ width: 90 }}>
                        <TableCell align="center" style={{ background }}>
                          {photo ? (
                            <img style={{ maxWidth: 90, maxHeight: 60 }} src={photo} alt="" />
                          ) : (
                            "No image"
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          <ItemId asset={asset} />
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          {asset.description}
                        </TableCell>
                        {columnVisibility.showModel && (
                          <TableCell align="center" style={{ background }}>
                            {asset.model}
                          </TableCell>
                        )}
                        {columnVisibility.showDimensions && (
                          <TableCell align="center" style={{ background }}>
                            <Dimensions dimensions={asset.dimensions} />
                          </TableCell>
                        )}
                        <TableCell align="center" style={{ background }}>
                          {asset.availableQuantity}
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          <Box width={140}>
                            <AssetQuantityField
                              quantity={value.quantity || ""}
                              onChange={quantity => handleChangeQuantity(asset.id, quantity)}
                              size="small"
                              disablePlus={value.quantity >= asset.availableQuantity}
                              disableMinus={value.quantity <= 1}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          <IconButton color="inherit" onClick={() => handleRemove(asset.id)}>
                            <Tooltip arrow title="Remove">
                              <HighlightOffIcon />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box
                p={3}
                bgcolor="#f4f6f8"
                borderRadius="4px"
                display="flex"
                justifyContent="center"
              >
                Empty
              </Box>
            )}
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box display="flex" p={1.5}>
          <Button variant="outlined" onClick={onClose} color="primary">
            Close
          </Button>
          <Button
            disabled={adding || disabled}
            sx={{ ml: 1 }}
            variant="contained"
            onClick={AddToCart}
            color="primary"
          >
            {isInventoryRequest ? "Add Items to Order" : "Add Items to Cart"}{" "}
            {adding && (
              <Box ml={1.5} clone>
                <CircularProgress size={18} style={{ color: "white" }} />
              </Box>
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
